'use client'

import React, { useEffect, useMemo, useState } from 'react'
import Link from 'next/link'
import { HomeOutlined } from '@ant-design/icons'
import { useTheme } from '@/shared/theme'

/**
 * Props interface for Breadcrumb component
 */
interface BreadcrumbItem {
  /** Display label for the breadcrumb item */
  label: string
  /** Optional href for navigation - if provided, item will be a link */
  href?: string
  /** Optional icon component to display before the label */
  icon?: React.ReactNode
}

interface BreadcrumbProps {
  /** Array of breadcrumb items to display */
  items: BreadcrumbItem[]
  /** Custom separator between breadcrumb items */
  separator?: string
  /** Additional CSS classes */
  className?: string
}

/**
 * Custom Breadcrumb component with clean styling and proper spacing
 *
 * Home href is provided by the parent (SubNavbar) so client vs employee
 * dashboard routing stays hydration-safe — no localStorage during render.
 *
 * @accessibility 44px min hit targets on links; readable contrast; reduced-motion
 */
export function Breadcrumb({
  items,
  separator = '›',
  className = ''
}: BreadcrumbProps) {
  const { token } = useTheme()
  const [reduceMotion, setReduceMotion] = useState(false)

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    setReduceMotion(mq.matches)
    const onChange = (e: MediaQueryListEvent) => setReduceMotion(e.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  const transition = useMemo(
    () =>
      reduceMotion
        ? 'none'
        : 'color 200ms ease, background-color 200ms ease, opacity 200ms ease',
    [reduceMotion]
  )

  if (!items || items.length === 0) {
    return null
  }

  // Fine-tech crumbs — tight row, current page carries weight
  const crumbBase: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 32,
    minWidth: 32,
    padding: '0 4px',
    borderRadius: 4,
    gap: 4,
    transition,
    boxSizing: 'border-box',
  }

  return (
    <nav
      className={className}
      aria-label="Breadcrumb navigation"
      role="navigation"
      style={{
        fontSize: 13,
        lineHeight: 1.3,
        color: token.colorText,
        minHeight: 32,
        display: 'block',
        visibility: 'visible',
        opacity: 1,
        zIndex: 1,
        backgroundColor: 'transparent',
        border: 'none',
        padding: 0,
      }}
    >
      <ol
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          margin: 0,
          padding: 0,
          listStyle: 'none',
          gap: 0,
        }}
      >
        {items.map((item, index) => {
          const isLast = index === items.length - 1
          const isLink = Boolean(item.href) && !isLast

          return (
            <React.Fragment key={`${item.label}-${index}`}>
              <li
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  margin: 0,
                  padding: 0,
                }}
              >
                {isLink && item.href ? (
                  <Link
                    href={item.href}
                    style={{
                      ...crumbBase,
                      color: token.colorTextSecondary,
                      textDecoration: 'none',
                      fontWeight: 500,
                      cursor: 'pointer',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = token.colorPrimary
                      e.currentTarget.style.backgroundColor = token.colorFillTertiary
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = token.colorTextSecondary
                      e.currentTarget.style.backgroundColor = 'transparent'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.outline = `2px solid ${token.colorPrimary}`
                      e.currentTarget.style.outlineOffset = '2px'
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.outline = 'none'
                    }}
                  >
                    {item.icon && (
                      <span
                        aria-hidden="true"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          fontSize: 13,
                          color: 'inherit',
                        }}
                      >
                        {item.icon}
                      </span>
                    )}
                    <span
                      style={{
                        whiteSpace: 'nowrap',
                        color: 'inherit',
                        fontSize: 13,
                        fontWeight: 500,
                      }}
                    >
                      {item.label}
                    </span>
                  </Link>
                ) : (
                  <span
                    aria-current={isLast ? 'page' : undefined}
                    style={{
                      ...crumbBase,
                      // Current page = sole page title in chrome (SubNavbar owns this)
                      color: isLast ? token.colorText : token.colorTextSecondary,
                      fontWeight: isLast ? 600 : 500,
                      fontSize: isLast ? 14 : 13,
                      letterSpacing: isLast ? '-0.015em' : undefined,
                      cursor: 'default',
                      minWidth: isLast ? 'auto' : 40,
                    }}
                  >
                    {item.icon && (
                      <span
                        aria-hidden="true"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          fontSize: 13,
                          color: 'inherit',
                        }}
                      >
                        {item.icon}
                      </span>
                    )}
                    <span
                      style={{
                        whiteSpace: 'nowrap',
                        color: isLast ? token.colorText : 'inherit',
                        fontSize: isLast ? 14 : 13,
                        fontWeight: isLast ? 600 : 500,
                        letterSpacing: isLast ? '-0.01em' : undefined,
                      }}
                    >
                      {item.label}
                    </span>
                  </span>
                )}
              </li>
              {!isLast && (
                <li
                  aria-hidden="true"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 4px',
                    minHeight: 32,
                    color: token.colorTextQuaternary || token.colorTextTertiary,
                    fontSize: 12,
                    userSelect: 'none',
                    fontWeight: 400,
                    opacity: 0.7,
                    pointerEvents: 'none',
                    lineHeight: 1,
                  }}
                >
                  {separator}
                </li>
              )}
            </React.Fragment>
          )
        })}
      </ol>
    </nav>
  )
}

/**
 * Default breadcrumb items for common navigation.
 * Home href is a placeholder; SubNavbar rewrites it to the correct
 * client/employee dashboard after mount (hydration-safe).
 */
export const createDefaultBreadcrumb = (currentPage: string): BreadcrumbItem[] => [
  {
    label: 'Home',
    href: '/',
    icon: <HomeOutlined />,
  },
  {
    label: currentPage,
  },
]

export default Breadcrumb
